<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcPropertyUnits',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    parent: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.parent,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parent');
    }
  );

  const act_2095089031469157_showModal_3wp2y3vvg_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'PropertyUnitEditor',
      props: {
        mode: 'add',
        parent: props.parent,
      },
    };
  };
  const act_2585509888553580_emit_eoppjzgyu_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2585509888553580_close_7bw5kjris_getWhen = async ($event) => {
    return !!$event.data.modalResult.ok;
  };
  const act_2585509888553580_showModal_smm2a6r8o_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'PropertyUnitEditor',
      props: {
        parent: props.parent,
        item: $event.scope.item,
      },
    };
  };

  const act_7315092382398562_reloadComponentData_53pq5wkfp =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_xy2z1jn4w =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_53pq5wkfp,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_xy2z1jn4w],
    event: 'reload',
  });
  const act_2095089031469157_showModal_3wp2y3vvg =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_3wp2y3vvg_getActionArgs,
    });
  const inst_evh_2095089031469157_clickPreventStop_bdourdq31 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_3wp2y3vvg,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_bdourdq31],
      event: 'click.prevent.stop',
    });
  const act_2585509888553580_emit_eoppjzgyu = new actionTypes.EmitAction({
    actionArgs: act_2585509888553580_emit_eoppjzgyu_getActionArgs,
  });
  const inst_evh_2585509888553580_close_7bw5kjris =
    new actionTypes.EventHandlerImpl({
      action: act_2585509888553580_emit_eoppjzgyu,
      event: 'close',
      when: inst_evh_2585509888553580_close_7bw5kjris_getWhen,
      displayName: 'emit',
    });
  const act_2585509888553580_showModal_smm2a6r8o =
    new actionTypes.ShowModalAction({
      actionArgs: act_2585509888553580_showModal_smm2a6r8o_getActionArgs,
      events: [inst_evh_2585509888553580_close_7bw5kjris],
    });
  const inst_evh_2585509888553580_clickPreventStop_ug94c8nwe =
    new actionTypes.EventHandlerImpl({
      action: act_2585509888553580_showModal_smm2a6r8o,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2585509888553580_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2585509888553580_clickPreventStop_ug94c8nwe],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_xy2z1jn4w,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_clickPreventStop_bdourdq31,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_2585509888553580_close_7bw5kjris,
    inst_evh_2585509888553580_clickPreventStop_ug94c8nwe,
    inst_evh_2585509888553580_clickPreventStop,
  };

  const evh_7315092382398562_reload_xy2z1jn4w = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_xy2z1jn4w.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_bdourdq31 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_bdourdq31.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2585509888553580_close_7bw5kjris = async (event, scope) => {
    return await actions.inst_evh_2585509888553580_close_7bw5kjris.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2585509888553580_clickPreventStop_ug94c8nwe = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2585509888553580_clickPreventStop_ug94c8nwe.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2585509888553580_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2585509888553580_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('Property', 'Units') }}
          </strong>
          <ItpButton
            :text="gettext('Add unit...')"
            variant="light"
            size="sm"
            class="ms-auto"
            v-if="!parent.readonly"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup>
        <BListGroupItem v-if="!parent.units || !parent.units.length">
          <ItpText>
            {{ pgettext('Property', 'No units have been added yet.') }}
          </ItpText>
        </BListGroupItem>
        <BListGroupItem
          :key="item.id"
          v-for="item in _.orderBy(parent.units, ['sort_order', 'name'])"
        >
          <ItpBox alignItems="center">
            <ItpText :text="item.name" class="fw-bold"> </ItpText>
            <ItpIcon
              :title="gettext('Locked')"
              name="fa-lock"
              class="px-3"
              v-if="item.locked"
            >
            </ItpIcon>
            <ItpButton
              icon="fa-pen"
              :tooltip="gettext('Edit')"
              variant="light"
              size="sm"
              class="ms-auto"
              v-if="!parent.readonly"
              @click.prevent.stop="
                evh_2585509888553580_clickPreventStop($event, { item })
              "
            >
            </ItpButton>
          </ItpBox>
          <ItpBox>
            <ItpText :text="item.description" pre> </ItpText>
          </ItpBox>
        </BListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
